import { useConfirmation } from "src/sudokit/components/ConfirmationDialog/ConfirmationProvider";
import { useState } from "react";
import InlineSVG from "react-inlinesvg";
import { useToast } from "src/components/ToastWrapper/index.js";
import { Dialog, DialogContent } from "src/sudokit/components/Dialog";
import { SudoButton } from "src/sudokit/components/SudoButton";
import MenuSelect from "src/sudokit/components/SudoSelect/MenuSelect";
import { Text } from "src/sudokit/components/Text";
import { PartnerCodeDetails } from "./PartnerCodeDetails";
import { PartnerCodeForm } from "./PartnerCodeForm";
import { PartnerCode, PartnerCodesFilter, useCreatePartnerCode, useDeletePartnerCode, usePartnerCodes, useUpdatePartnerCode } from "./PartnerCodesQueries";
import { PartnerCodesTable } from "./PartnerCodesTable";
import IconButton from "src/components/IconButton";
import { cn } from "src/lib/cn";

export default function PartnerCodes() {
  const confirmAction = useConfirmation();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCode, setEditingCode] = useState<PartnerCode | null>(null);
  const [viewingCode, setViewingCode] = useState<PartnerCode | null>(null);
  const [filters, setFilters] = useState<PartnerCodesFilter>({});
  const { setToast } = useToast();

  const { data: partnerCodes = [], isLoading, isRefetching, refetch } = usePartnerCodes(filters);

  const createMutation = useCreatePartnerCode();
  const updateMutation = useUpdatePartnerCode();
  const deleteMutation = useDeletePartnerCode();

  const handleCreate = async (data: any) => {
    try {
      await createMutation.mutateAsync(data);
      setToast("Partner code created successfully");
      setShowCreateForm(false);
    } catch (error: any) {
      setToast(error.message || "Failed to create partner code");
    }
  };

  const handleUpdate = async (data: any) => {
    try {
      await updateMutation.mutateAsync(data);
      setToast("Partner code updated successfully");
      setEditingCode(null);
    } catch (error: any) {
      setToast(error.message || "Failed to update partner code");
    }
  };

  const handleDelete = async (code: PartnerCode) => {
    await confirmAction({
      title: "Delete partner code",
      message: `Are you sure you want to delete "${code.code}"?`,
      actionLabel: "Delete",
      destructive: true
    }, async () => {
      try {
        await deleteMutation.mutateAsync(code.id);
        setToast("Partner code deleted successfully");
      } catch (error: any) {
        setToast(error.message || "Failed to delete partner code");
      }
    });
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pt-6">
      <div className="sm:flex sm:items-center mb-8">
        <div className="sm:flex-auto gap-1 flex-col flex">
          <Text className="text-3xl font-semibold bg-gradient-to-r from-app-grey-0 to-app-grey-1 bg-clip-text text-transparent">Partner Codes</Text>
          <Text className="text-base text-app-grey-1">Manage partner and event codes that provide bonus credits to new subscribers</Text>
        </div>
      </div>

      <div className="mt-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MenuSelect
            label="Filter by type"
            value={filters.type || "all"}
            onChange={(value) => setFilters({ ...filters, type: value === "all" ? undefined : (value as any) })}
            options={[
              { value: "all", label: "All Types" },
              { value: "partner", label: "Partners" },
              { value: "event", label: "Events" },
            ]}
            placeholder="Filter by type"
          />
          <MenuSelect
            label="Filter by status"
            value={filters.is_active === undefined ? "all" : filters.is_active ? "active" : "inactive"}
            onChange={(value) =>
              setFilters({
                ...filters,
                is_active: value === "all" ? undefined : value === "active",
              })
            }
            options={[
              { value: "all", label: "All Status" },
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" },
            ]}
            placeholder="Filter by status"
          />

          <IconButton
            onClick={refetch}
            disabled={isRefetching || isLoading}
            iconSize={15}
            className={cn(isRefetching || isLoading ? "cursor-not-allowed animate-spin" : "")}
            src="/assets/brainstorm/refresh.svg"
          />
        </div>

        <SudoButton
          variant="tertiary"
          onClick={() => setShowCreateForm(true)}
          size="small"
          rightIcon="/assets/add.svg"
          className="bg-app-primary-cta-translucent"
        >
          Add a new code
        </SudoButton>
      </div>

      <div className="mt-4">
        {isLoading && !partnerCodes.length ? (
          <div className="flex items-center justify-center py-32">
            <div className="text-center">
              <InlineSVG
                src="/assets/loader.svg"
                className="h-12 w-12 mx-auto mb-4 animate-spin [&_path]:fill-app-primary-cta"
              />
              <p className="text-app-grey-1 text-sm font-medium">Loading partner codes...</p>
            </div>
          </div>
        ) : partnerCodes.length === 0 ? (
          <div className="text-center py-4 px-4 bg-app-menu-color rounded-xl border border-app-menu-color-hover shadow-card animate-in fade-in">
            <Text>Couldn't find any codes for this filter... Try changing the dials and knobs above to see if you might find what you're looking for 🤔</Text>
          </div>
        ) : (
          <PartnerCodesTable
            partnerCodes={partnerCodes}
            onEdit={setEditingCode}
            onDelete={handleDelete}
            onViewDetails={setViewingCode}
          />
        )}
      </div>

      <Dialog
        open={showCreateForm}
        onOpenChange={setShowCreateForm}
      >
        <DialogContent className="sm:max-w-lg">
          <PartnerCodeForm
            onSubmit={handleCreate}
            onCancel={() => setShowCreateForm(false)}
            isSubmitting={createMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!editingCode}
        onOpenChange={(open) => !open && setEditingCode(null)}
      >
        <DialogContent className="sm:max-w-lg">
          {editingCode && (
            <PartnerCodeForm
              code={editingCode}
              onSubmit={handleUpdate}
              onCancel={() => setEditingCode(null)}
              isSubmitting={updateMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!viewingCode}
        onOpenChange={(open) => !open && setViewingCode(null)}
      >
        <DialogContent className="sm:max-w-xl">
          {viewingCode && (
            <PartnerCodeDetails
              code={viewingCode}
              onClose={() => setViewingCode(null)}
              onEdit={() => {
                setEditingCode(viewingCode);
                setViewingCode(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
